import { Component, input, model, output, signal } from '@angular/core';
import { NotifyIcons, Tab } from '@agilox/ui-common';
import { IconModule } from '../icon/icon.module';
import { TooltipModule } from '../tooltip/tooltip.module';

@Component({
	selector: 'ui-tabs',
	imports: [IconModule, TooltipModule],
	templateUrl: './tabs.component.html',
})
export class TabsComponent {
	tabs = input<Tab[]>([]);
	tabIndex = input<number>(0);
	selectedIndex = model<number>(0);

	changedTab = output<number>();

	protected readonly warningIcon = NotifyIcons.warning;

	private _focusedIndex = signal<number>(-1);

	tabChanged(index: number) {
		if (this.selectedIndex() === index || this.tabs()[index].disabled) {
			return;
		}
		this.selectedIndex.set(index);
		this.changedTab.emit(index);
	}

	onKeyDown(event: KeyboardEvent, currentIndex: number): void {
		let newIndex = currentIndex;
		switch (event.key) {
			case 'ArrowLeft':
			case 'ArrowUp':
				event.preventDefault();
				newIndex = this.getPreviousEnabledIndex(currentIndex);
				break;
			case 'ArrowRight':
			case 'ArrowDown':
				event.preventDefault();
				newIndex = this.getNextEnabledIndex(currentIndex);
				break;
			default:
				return;
		}

		if (newIndex !== -1 && newIndex !== currentIndex) {
			this._focusedIndex.set(newIndex);
			this.focusTab(newIndex);
		}
	}

	private getPreviousEnabledIndex(currentIndex: number): number {
		const tabs = this.tabs();
		for (let i = currentIndex - 1; i >= 0; i--) {
			if (!tabs[i].disabled) {
				return i;
			}
		}
		for (let i = tabs.length - 1; i > currentIndex; i--) {
			if (!tabs[i].disabled) {
				return i;
			}
		}
		return currentIndex;
	}

	private getNextEnabledIndex(currentIndex: number): number {
		const tabs = this.tabs();
		for (let i = currentIndex + 1; i < tabs.length; i++) {
			if (!tabs[i].disabled) {
				return i;
			}
		}
		for (let i = 0; i < currentIndex; i++) {
			if (!tabs[i].disabled) {
				return i;
			}
		}
		return currentIndex;
	}

	private focusTab(index: number): void {
		setTimeout(() => {
			const tabElement = document.querySelector(
				`[data-cy="ui-tabs-${this.tabs()[index].key}"]`
			) as HTMLButtonElement;
			if (tabElement) {
				tabElement.focus();
			}
		}, 0);
	}

	getTabTabIndex(index: number): number {
		const tabs = this.tabs();

		if (tabs[index].disabled) {
			return -1;
		}

		return this.tabIndex();
	}

	onFocus(index: number): void {
		this._focusedIndex.set(index);
	}
}
